/**
 * Workflows Page
 *
 * Displays:
 * - Workflow List (from Workflow Registry)
 * - Workflow Status
 * - Execute Button
 * - Workflow Configuration Modal
 */

import React, { useEffect, useState } from 'react';
import { apiClient, WorkflowRegistry, Workflow } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../utils/toast';
import { logger } from '../utils/logger';
import { TIMEOUTS } from '../constants/timeouts';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { WorkflowModal } from '../components/WorkflowModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { getConfigComponentFromTags } from '../components/workflow-configs/WorkflowConfigRegistry';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Play, PauseCircle, PlayCircle, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';

export const Workflows: React.FC = () => {
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState<WorkflowRegistry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executingWorkflowId, setExecutingWorkflowId] = useState<string | null>(null);

  // Modal state
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [selectedRegistryId, setSelectedRegistryId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // Toggle active state
  const [toggleWorkflow, setToggleWorkflow] = useState<WorkflowRegistry | null>(null);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const fetchWorkflows = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const workflowsData = await apiClient.getWorkflowRegistry();
      setWorkflows(workflowsData);

      logger.info('Fetched workflows from registry', {
        totalWorkflows: workflowsData.length,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
      logger.error('Failed to fetch workflows:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchWorkflows();

      // Auto-refresh workflows every 30 seconds
      const intervalId = setInterval(() => {
        logger.debug('Auto-refreshing workflows');
        fetchWorkflows();
      }, 30000); // 30 seconds

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [user]);

  const handleExecute = async (workflowId: string) => {
    try {
      setExecutingWorkflowId(workflowId);

      logger.info('Executing workflow from registry', {
        workflowId,
      });

      const result = await apiClient.executeRegisteredWorkflow(workflowId);

      if (result.success) {
        showToast.success('Workflow executed successfully');
      } else {
        showToast.error(`Workflow execution failed: ${result.error}`);
      }

      // Refresh workflows after delay to show updated stats
      setTimeout(() => {
        fetchWorkflows();
      }, TIMEOUTS.EXECUTION_REFRESH_DELAY);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to execute workflow';

      logger.error('Workflow execution failed', {
        workflowId,
        error: message
      });

      showToast.error(`Failed to execute workflow: ${message}`);
    } finally {
      setExecutingWorkflowId(null);
    }
  };

  // Convert WorkflowRegistry to Workflow format for modal
  const convertRegistryToWorkflow = (registry: WorkflowRegistry): Workflow => {
    return {
      id: registry.workflow_id_n8n,
      name: registry.display_name,
      active: registry.is_active,
      nodes: [],
      connections: {},
      createdAt: registry.created_at,
      updatedAt: registry.updated_at,
      tags: registry.tags.map((tag: string, index: number) => ({
        id: index,
        name: tag,
      })),
    };
  };

  // Modal handlers
  const handleWorkflowClick = (workflow: WorkflowRegistry) => {
    const convertedWorkflow = convertRegistryToWorkflow(workflow);
    setSelectedWorkflow(convertedWorkflow);
    setSelectedRegistryId(workflow.id);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    if (!isModalLoading) {
      setIsModalOpen(false);
      setSelectedWorkflow(null);
      setSelectedRegistryId(null);
    }
  };

  const handleWorkflowExecute = async (data: Record<string, unknown>) => {
    if (!selectedRegistryId) return;

    try {
      setIsModalLoading(true);

      logger.info('Executing workflow from modal', {
        registryId: selectedRegistryId,
        workflowName: selectedWorkflow?.name,
        hasData: Object.keys(data).length > 0
      });

      const result = await apiClient.executeRegisteredWorkflow(selectedRegistryId, data);

      if (result.success) {
        showToast.success(`Workflow executed successfully`);
      } else {
        showToast.error(`Workflow execution failed: ${result.error}`);
      }

      // Refresh workflows after delay
      setTimeout(() => {
        fetchWorkflows();
      }, TIMEOUTS.EXECUTION_REFRESH_DELAY);

      // Close modal on success
      setIsModalOpen(false);
      setSelectedWorkflow(null);
      setSelectedRegistryId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to execute workflow';

      logger.error('Workflow execution failed from modal', {
        registryId: selectedRegistryId,
        workflowName: selectedWorkflow?.name,
        error: message,
        err
      });

      showToast.error(`Failed to execute workflow: ${message}`);
    } finally {
      setIsModalLoading(false);
    }
  };

  // Toggle handlers
  const handleToggleClick = (workflow: WorkflowRegistry) => {
    setToggleWorkflow(workflow);
    setIsToggleModalOpen(true);
  };

  const handleToggleConfirm = async () => {
    if (!toggleWorkflow) return;

    try {
      setIsToggling(true);
      const newActiveState = !toggleWorkflow.is_active;

      logger.info('Toggling workflow active status', {
        workflowId: toggleWorkflow.workflow_id_n8n,
        workflowName: toggleWorkflow.display_name,
        newActiveState,
      });

      await apiClient.setWorkflowActive(toggleWorkflow.workflow_id_n8n, newActiveState);

      showToast.success(
        newActiveState
          ? `Workflow "${toggleWorkflow.display_name}" wurde aktiviert`
          : `Workflow "${toggleWorkflow.display_name}" wurde deaktiviert`
      );

      // Update local state
      setWorkflows((prev) =>
        prev.map((wf) =>
          wf.id === toggleWorkflow.id ? { ...wf, is_active: newActiveState } : wf
        )
      );

      // Refresh workflows after delay
      setTimeout(() => {
        fetchWorkflows();
      }, TIMEOUTS.EXECUTION_REFRESH_DELAY);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Aktualisieren des Workflow-Status';
      logger.error('Failed to toggle workflow active status', {
        workflowId: toggleWorkflow.workflow_id_n8n,
        error: message,
      });
      showToast.error(message);
    } finally {
      setIsToggling(false);
      setIsToggleModalOpen(false);
      setToggleWorkflow(null);
    }
  };

  const handleToggleCancel = () => {
    if (!isToggling) {
      setIsToggleModalOpen(false);
      setToggleWorkflow(null);
    }
  };

  // Generate confirmation modal content based on action
  const getToggleModalContent = () => {
    if (!toggleWorkflow) {
      return { title: '', message: '' };
    }

    if (toggleWorkflow.is_active) {
      // Deactivating
      return {
        title: 'Workflow deaktivieren',
        message: `Sind Sie sicher, dass Sie den Workflow "${toggleWorkflow.display_name}" deaktivieren möchten? Alle damit verbundenen automatisierten Prozesse werden gestoppt und sind nicht mehr funktionsfähig, bis der Workflow erneut aktiviert wird.`,
      };
    } else {
      // Activating
      return {
        title: 'Workflow aktivieren',
        message: `Sind Sie sicher, dass Sie den Workflow "${toggleWorkflow.display_name}" aktivieren möchten? Nach der Aktivierung werden automatisierte Prozesse gestartet, die möglicherweise API-Kosten verursachen und Ressourcen verbrauchen können.`,
      };
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading workflows..." />;
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={() => window.location.reload()} />;
  }

  const getStatusBadge = (status: string | null) => {
    if (!status) {
      return <Badge variant="outline">Not executed</Badge>;
    }

    switch (status) {
      case 'success':
        return (
          <Badge className="bg-primary/10 text-primary border-primary/20">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" /> Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-secondary/10 text-secondary border-secondary/20">
            <Clock className="w-3 h-3 mr-1" /> Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Workflows</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Execute registered workflows
            </p>
          </div>
          <Button variant="outline" onClick={fetchWorkflows}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {workflows.length === 0 ? (
          <Card className="p-6">
            <p className="text-muted-foreground">
              No workflows registered yet. Contact your administrator to register workflows.
            </p>
          </Card>
        ) : (
          <Card>
            <ul className="divide-y divide-border">
              {workflows.map((workflow) => (
                <li key={workflow.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => handleWorkflowClick(workflow)}
                      >
                        <div className="flex-shrink-0">
                          {getStatusBadge(workflow.last_execution_status)}
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="text-sm font-medium text-primary hover:text-primary/80">
                            {workflow.display_name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {workflow.description && (
                              <div className="mt-1">{workflow.description}</div>
                            )}
                            <div className="mt-1 flex items-center space-x-4">
                              <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">
                                {workflow.workflow_id_n8n}
                              </span>
                              {workflow.tags && workflow.tags.length > 0 && (
                                <span className="text-xs">
                                  Tags: {workflow.tags.join(', ')}
                                </span>
                              )}
                              <span className="text-xs">
                                Executions: {workflow.trigger_count}
                              </span>
                              {workflow.last_triggered_at && (
                                <span className="text-xs">
                                  Last: {new Date(workflow.last_triggered_at).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {/* Active Status Indicator */}
                        <Badge variant={workflow.is_active ? 'default' : 'secondary'}>
                          {workflow.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>

                        {/* Toggle Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleClick(workflow);
                          }}
                          disabled={isToggling && toggleWorkflow?.id === workflow.id}
                          title={workflow.is_active ? 'Workflow deaktivieren' : 'Workflow aktivieren'}
                          className={workflow.is_active
                            ? 'border-secondary/30 text-secondary hover:bg-secondary/10'
                            : 'border-primary/30 text-primary hover:bg-primary/10'
                          }
                        >
                          {isToggling && toggleWorkflow?.id === workflow.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : workflow.is_active ? (
                            <PauseCircle className="w-4 h-4" />
                          ) : (
                            <PlayCircle className="w-4 h-4" />
                          )}
                        </Button>

                        {/* Execute Button */}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExecute(workflow.id);
                          }}
                          disabled={executingWorkflowId === workflow.id}
                          size="sm"
                        >
                          {executingWorkflowId === workflow.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Executing...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Execute
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Workflow Modal */}
        <WorkflowModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          workflow={selectedWorkflow}
          isLoading={isModalLoading}
        >
          {selectedWorkflow && (() => {
            const ConfigComponent = getConfigComponentFromTags(selectedWorkflow.tags);
            return (
              <ConfigComponent
                workflow={selectedWorkflow}
                onExecute={handleWorkflowExecute}
                onClose={handleModalClose}
                isLoading={isModalLoading}
              />
            );
          })()}
        </WorkflowModal>

        {/* Toggle Confirmation Modal */}
        <ConfirmationModal
          isOpen={isToggleModalOpen}
          onClose={handleToggleCancel}
          onConfirm={handleToggleConfirm}
          title={getToggleModalContent().title}
          message={getToggleModalContent().message}
          confirmLabel="Fortfahren"
          cancelLabel="Abbrechen"
          variant={toggleWorkflow?.is_active ? 'warning' : 'info'}
          isLoading={isToggling}
        />
      </div>
    </div>
  );
};













